/**
 * The job an accepted quote opened (contractor pages phase 3): where it is
 * (Accepted → In progress → Completion reported → Confirmed / Disputed /
 * Cancelled), what it is worth, the success fee it will carry, and the one
 * next step — Start, Mark complete, or Cancel before completion is reported.
 */
import { useState } from 'react';
import type { LeadProjectSummary, LeadQuoteSummary } from '../api/types';
import { useSession } from '../auth/session';
import { Link } from '../router';
import { Field, formatDate } from '../ui/kit';
import { useMutation } from '../ui/hooks';
import { formatBps, formatCents, netCents, successFeeCents } from './feeMath';
import { QuoteError } from './QuoteComposer';

/** Days of homeowner silence after "Mark complete" before the job confirms itself (`PROJECT_AUTO_CONFIRM_DAYS`). */
export const AUTO_CONFIRM_DAYS = 7;
const NOTE_MAX = 2000;
const REASON_MAX = 500;

export const PROJECT_STATUS_LABEL: Record<LeadProjectSummary['status'], string> = {
  accepted: 'Accepted',
  in_progress: 'In progress',
  completed_reported: 'Completion reported',
  completed_confirmed: 'Confirmed',
  disputed: 'Disputed',
  cancelled: 'Cancelled',
};

const STATUS_TONE: Record<LeadProjectSummary['status'], string> = {
  accepted: 'info',
  in_progress: 'info',
  completed_reported: 'warn',
  completed_confirmed: 'ok',
  disputed: 'bad',
  cancelled: '',
};

const CONFIRMED_BY: Record<NonNullable<LeadProjectSummary['confirmed_by']>, string> = {
  homeowner: 'Confirmed by the homeowner',
  auto: `Confirmed automatically (no reply in ${AUTO_CONFIRM_DAYS} days)`,
  operator: 'Confirmed by FinderlyFix',
};

interface Step {
  key: string;
  label: string;
  at: string | null;
  state: 'done' | 'current' | 'todo' | 'stopped';
}

function steps(project: LeadProjectSummary, quote: LeadQuoteSummary | null): Step[] {
  const s = project.status;
  const out: Step[] = [{ key: 'accepted', label: 'Quote accepted', at: quote?.accepted_at ?? null, state: 'done' }];
  const started = !!project.started_at || ['completed_reported', 'completed_confirmed', 'disputed'].includes(s);
  const reported = !!project.reported_at || ['completed_confirmed', 'disputed'].includes(s);
  if (s === 'cancelled') {
    if (started) out.push({ key: 'in_progress', label: 'Work started', at: project.started_at, state: 'done' });
    if (reported) out.push({ key: 'completed_reported', label: 'Completion reported', at: project.reported_at, state: 'done' });
    out.push({ key: 'cancelled', label: project.cancel_reason ? `Cancelled: ${project.cancel_reason}` : 'Cancelled', at: project.cancelled_at, state: 'stopped' });
    return out;
  }
  out.push({ key: 'in_progress', label: 'Work started', at: project.started_at, state: started ? 'done' : s === 'accepted' ? 'current' : 'todo' });
  out.push({
    key: 'completed_reported',
    label: 'Completion reported',
    at: project.reported_at,
    state: reported ? 'done' : s === 'in_progress' ? 'current' : 'todo',
  });
  if (s === 'completed_confirmed') {
    out.push({ key: 'confirmed', label: project.confirmed_by ? CONFIRMED_BY[project.confirmed_by] : 'Confirmed', at: project.confirmed_at, state: 'done' });
  } else if (s === 'disputed') {
    out.push({ key: 'disputed', label: 'Homeowner reported a problem', at: project.disputed_at, state: 'stopped' });
  } else {
    out.push({ key: 'confirmed', label: 'Homeowner confirms', at: null, state: s === 'completed_reported' ? 'current' : 'todo' });
  }
  return out;
}

export interface ProjectCardProps {
  project: LeadProjectSummary;
  /** The accepted quote, for its acceptance date. */
  quote: LeadQuoteSummary | null;
  /** The `quotes` capability: Start / Mark complete / Cancel. */
  canAct: boolean;
  onChanged: () => void;
}

export function ProjectCard({ project, quote, canAct, onChanged }: ProjectCardProps) {
  const { api } = useSession();
  const [panel, setPanel] = useState<'none' | 'complete' | 'cancel'>('none');
  const [note, setNote] = useState('');
  const [reason, setReason] = useState('');
  const [reasonTried, setReasonTried] = useState(false);
  const value = project.final_value_cents ?? project.contract_value_cents;
  const bps = project.success_fee_bps;
  const pct = formatBps(bps);
  const fee = successFeeCents(value, bps);
  const done = () => {
    setPanel('none');
    onChanged();
  };
  const start = useMutation(async () => {
    await api.startProject(project.id);
    done();
  });
  const complete = useMutation(async () => {
    await api.completeProject(project.id, note.trim() || undefined);
    done();
  });
  const cancel = useMutation(async () => {
    await api.cancelProject(project.id, reason.trim());
    done();
  });
  const pending = start.pending || complete.pending || cancel.pending;
  const open = project.status === 'accepted' || project.status === 'in_progress';
  const list = steps(project, quote);

  return (
    <div className="stack">
      <div className="row">
        <span className={`tag ${STATUS_TONE[project.status]}`.trim()}>{PROJECT_STATUS_LABEL[project.status]}</span>
      </div>
      <ol className="timeline" aria-label="Job progress">
        {list.map((step) => (
          <li
            key={step.key}
            className={step.state === 'done' ? 't-accepted' : step.state === 'stopped' ? 't-declined' : ''}
            aria-current={step.state === 'current' ? 'step' : undefined}
          >
            <span className="n" aria-hidden="true" />
            <span className={step.state === 'todo' ? 'muted' : undefined} style={{ fontWeight: step.state === 'todo' ? 400 : 600 }}>
              {step.label}
            </span>
            {step.at ? <span className="small muted"> · {formatDate(step.at)}</span> : null}
            {step.state === 'todo' ? <span className="visually-hidden"> (not yet)</span> : null}
            {step.state === 'current' ? <span className="small muted"> · next</span> : null}
          </li>
        ))}
      </ol>

      <dl className="facts">
        <dt>Contract value</dt>
        <dd>
          {formatCents(value)}
          {project.final_value_cents !== null && project.final_value_cents !== project.contract_value_cents ? (
            <span className="small muted"> (adjusted from {formatCents(project.contract_value_cents)})</span>
          ) : null}
        </dd>
        <dt>Success fee ({pct})</dt>
        <dd>{project.status === 'cancelled' ? <span className="muted">None, the job was cancelled</span> : formatCents(fee)}</dd>
        {project.status === 'cancelled' ? null : (
          <>
            <dt>You keep</dt>
            <dd>{formatCents(netCents(value, bps))}</dd>
          </>
        )}
      </dl>

      {project.status === 'completed_reported' ? (
        <p className="notice info" style={{ margin: 0 }}>
          Waiting for the homeowner to confirm the job is done. If they don’t reply, it’s confirmed automatically
          {project.auto_confirm_at ? ` on ${formatDate(project.auto_confirm_at)}` : ` after ${AUTO_CONFIRM_DAYS} days`} and the {pct} success fee (
          {formatCents(fee)}) is added to your statement.
        </p>
      ) : null}

      {project.status === 'disputed' ? (
        <div className="notice warn" role="status">
          <div className="stack-sm" style={{ gap: 2 }}>
            <strong>FinderlyFix is reviewing this job; no fee is charged while the dispute is open.</strong>
            {project.dispute_reason ? <span>The homeowner said: “{project.dispute_reason}”</span> : null}
            <span className="small">We’ll contact you and the homeowner, and tell you what we decide.</span>
          </div>
        </div>
      ) : null}

      {project.status === 'completed_confirmed' ? (
        <p className="notice ok" style={{ margin: 0 }}>
          <span>
            The job is confirmed complete. The {pct} success fee of <strong>{formatCents(fee)}</strong> is on your statement.{' '}
            <Link to="/billing">View billing</Link>
          </span>
        </p>
      ) : null}

      {project.status === 'cancelled' ? (
        <p className="muted" style={{ margin: 0 }}>
          This job was cancelled{project.cancelled_at ? ` on ${formatDate(project.cancelled_at)}` : ''}. No success fee is charged.
        </p>
      ) : null}

      {project.completion_note && project.status !== 'cancelled' ? (
        <div className="sunk">
          <strong>Your completion note</strong>
          <p style={{ margin: '4px 0 0', whiteSpace: 'pre-wrap' }}>{project.completion_note}</p>
        </div>
      ) : null}

      {canAct && open && panel === 'none' ? (
        <div className="row">
          {project.status === 'accepted' ? (
            <button type="button" className="btn btn-primary" disabled={pending} onClick={() => void start.run()}>
              {start.pending ? 'Starting…' : 'Start the job'}
            </button>
          ) : null}
          <button
            type="button"
            className={project.status === 'accepted' ? 'btn' : 'btn btn-primary'}
            disabled={pending}
            onClick={() => setPanel('complete')}
          >
            Mark complete…
          </button>
          <button type="button" className="btn btn-ghost" disabled={pending} onClick={() => setPanel('cancel')}>
            Cancel the job…
          </button>
        </div>
      ) : null}

      {canAct && open && panel === 'complete' ? (
        <div className="sunk stack-sm">
          <p style={{ margin: 0 }}>
            The homeowner gets an email to confirm. If they do nothing for {AUTO_CONFIRM_DAYS} days, the job is confirmed and the {pct} success fee (
            {formatCents(fee)}) is added to your statement.
          </p>
          <Field label="Note for the homeowner" optional hint="What you did, anything they should know.">
            {(p) => <textarea {...p} className="input" rows={3} maxLength={NOTE_MAX} value={note} onChange={(e) => setNote(e.target.value)} />}
          </Field>
          <QuoteError error={complete.error} />
          <div className="row">
            <button type="button" className="btn btn-primary" disabled={pending} onClick={() => void complete.run()}>
              {complete.pending ? 'Sending…' : 'Mark complete and ask the homeowner to confirm'}
            </button>
            <button type="button" className="btn" disabled={pending} onClick={() => setPanel('none')}>
              Back
            </button>
          </div>
        </div>
      ) : null}

      {canAct && open && panel === 'cancel' ? (
        <div className="sunk stack-sm">
          <p style={{ margin: 0 }}>Cancelling closes the job without a success fee. Only cancel if the work won’t go ahead.</p>
          <Field
            label="Why is the job cancelled?"
            issues={reasonTried && !reason.trim() ? [{ path: 'reason', message: 'Say why, so FinderlyFix has it on file.' }] : []}
          >
            {(p) => <textarea {...p} className="input" rows={2} maxLength={REASON_MAX} value={reason} onChange={(e) => setReason(e.target.value)} />}
          </Field>
          <QuoteError error={cancel.error} />
          <div className="row">
            <button
              type="button"
              className="btn btn-danger"
              disabled={pending}
              onClick={() => {
                setReasonTried(true);
                if (reason.trim()) void cancel.run();
              }}
            >
              {cancel.pending ? 'Cancelling…' : 'Cancel the job'}
            </button>
            <button type="button" className="btn" disabled={pending} onClick={() => setPanel('none')}>
              Back
            </button>
          </div>
        </div>
      ) : null}

      <QuoteError error={start.error} />
    </div>
  );
}
